import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BRANCH_LABELS } from '@lorsamthai/ops-shared';
import {
  cancelPurchaseOrder,
  getPurchaseOrder,
  markPoStuck,
  receivePurchaseOrder,
  type PurchaseOrderDoc,
} from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtDateTime, fmtDate, fmtInt } from '../lib/format.js';
import { useAuth } from '../auth/context.js';

export default function PODetail() {
  const { id = '' } = useParams();
  const poId = Number(id);
  const { me } = useAuth();
  const [po, setPo] = useState<PurchaseOrderDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [receiving, setReceiving] = useState(false);
  const [recvQty, setRecvQty] = useState<Record<string, number>>({});

  const load = async () => {
    try {
      const data = await getPurchaseOrder(poId);
      setPo(data);
      // Initialize receive inputs to qty_received (no change)
      const init: Record<string, number> = {};
      for (const it of data.purchase_order_items) init[it.sku] = it.qty_received;
      setRecvQty(init);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  if (error) return <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  if (!po) return <div className="text-sm text-slate-500">กำลังโหลด...</div>;

  const today = new Date().toISOString().slice(0, 10);
  const isStuck = po.stuck_after && po.stuck_after < today &&
                  ['approved', 'ordered'].includes(po.status);

  const canEdit = me?.role === 'super_admin' &&
                  ['approved', 'ordered'].includes(po.status);

  const doReceive = async (markAll: boolean) => {
    setReceiving(true);
    try {
      const lines = po.purchase_order_items.map((it) => ({
        sku: it.sku,
        qty_received: markAll ? it.qty_ordered : (recvQty[it.sku] ?? it.qty_received),
      }));
      await receivePurchaseOrder(po.id, { lines });
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'receive failed');
    } finally {
      setReceiving(false);
    }
  };

  const doMarkStuck = async () => {
    const reason = window.prompt('เหตุผล (เช่น supplier เลิกผลิต)');
    if (reason === null) return;
    try {
      await markPoStuck(po.id, reason || undefined);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'failed');
    }
  };

  const doCancel = async () => {
    const reason = window.prompt('เหตุผลการยกเลิก');
    if (!reason) return;
    try {
      await cancelPurchaseOrder(po.id, reason);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'failed');
    }
  };

  const supplierCopyText = po.purchase_order_items
    .map((it) => `${it.sku} ${it.qty_ordered}`)
    .join('\n');

  return (
    <div className="space-y-5">
      <div>
        <Link to="/pos" className="text-xs text-slate-500 hover:underline">← กลับรายการ PO</Link>
        <h1 className="mt-1 text-xl font-bold">
          {po.po_ref ?? `PO #${po.id}`}
        </h1>
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
          <Badge tone={po.status === 'received' ? 'ok' : isStuck ? 'danger' : 'info'}>
            {isStuck ? 'ค้าง' : po.status}
          </Badge>
          <span>สาขา: {BRANCH_LABELS[po.dest_branch]}</span>
          <span>สร้าง: {fmtDateTime(po.created_at)}</span>
          {po.ordered_at && <span>สั่ง: {fmtDateTime(po.ordered_at)}</span>}
          {po.eta_date_promised && <span>ETA: {fmtDate(po.eta_date_promised)}</span>}
          {po.stuck_after && <span>stuck_after: {fmtDate(po.stuck_after)}</span>}
        </div>
        {po.note && (
          <div className="mt-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600">{po.note}</div>
        )}
      </div>

      <section className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">SKU</th>
              <th className="px-3 py-2 text-right">สั่ง</th>
              <th className="px-3 py-2 text-right">รับแล้ว</th>
              <th className="px-3 py-2 text-right">ค้าง</th>
              {canEdit && <th className="px-3 py-2 text-right">รับเพิ่ม</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {po.purchase_order_items.map((it) => {
              const pending = it.qty_ordered - it.qty_received;
              return (
                <tr key={it.sku}>
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/stock/${encodeURIComponent(it.sku)}`} className="hover:underline">
                      {it.sku}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(it.qty_ordered)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(it.qty_received)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {pending > 0 ? <Badge tone="warn">{pending}</Badge> : <Badge tone="ok">ครบ</Badge>}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={it.qty_received}
                        max={it.qty_ordered}
                        value={recvQty[it.sku] ?? it.qty_received}
                        onChange={(e) =>
                          setRecvQty((p) => ({ ...p, [it.sku]: Number(e.target.value) }))
                        }
                        className="w-16 rounded-md border border-slate-200 px-2 py-1 text-right text-xs"
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => doReceive(false)}
            disabled={receiving}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
          >
            บันทึกรับบางส่วน
          </button>
          <button
            onClick={() => doReceive(true)}
            disabled={receiving}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
          >
            รับครบทั้งใบ
          </button>
          <button
            onClick={doMarkStuck}
            className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
          >
            Mark stuck (ยกเลิก pipeline)
          </button>
          <button
            onClick={doCancel}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            ยกเลิก PO
          </button>
        </div>
      )}

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold">ข้อความส่งให้ supplier</h2>
        <pre className="mt-2 rounded-md bg-slate-50 p-3 text-xs font-mono whitespace-pre-wrap">
{supplierCopyText}
        </pre>
        <button
          onClick={() => {
            void navigator.clipboard.writeText(supplierCopyText);
          }}
          className="mt-2 rounded-md border border-slate-200 px-3 py-1 text-xs hover:bg-slate-50"
        >
          คัดลอก
        </button>
      </section>
    </div>
  );
}
